/**
 * Guilds — lista de guilds do servidor (menu próprio, extraído da antiga
 * Ferramentas GM). Por enquanto só lista; futuramente ganha ações por guild.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Users2, RefreshCw, Search, X, AlertCircle } from 'lucide-react';
import { listGuilds, type GuildRow } from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';
import { DataTable } from '@/shared/components/ui/DataTable';
import { useTableSort } from '@/shared/hooks/useTableSort';

export default function GuildsPage() {
  const { notify } = useToast();
  const [guilds, setGuilds] = useState<GuildRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      setGuilds(await listGuilds());
      setError(null);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro ao carregar guilds');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return guilds;
    return guilds.filter((g) =>
      g.name.toLowerCase().includes(s) || String(g.guildNo).includes(s));
  }, [guilds, search]);
  const { sorted, sort, toggle } = useTableSort(filtered, { key: 'members', order: 'desc' });

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Users2 className="text-[#00e1d7]" size={26} /> Guilds
          </h1>
          <p className="text-sm text-white/65 mt-1">
            {guilds.length.toLocaleString('pt-BR')} guilds no servidor
          </p>
        </div>
        <button
          onClick={fetchData} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                     bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7]
                     hover:bg-[#00e1d7]/20 transition cursor-pointer disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      <div className="surface-card rounded-xl p-3 mb-4 flex items-center gap-3">
        <Search size={16} className="text-white/45 ml-1" />
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome ou nº da guild..."
          className="flex-1 bg-transparent text-sm text-white placeholder:text-white/35 outline-none"
        />
        {search && (
          <button onClick={() => setSearch('')} className="text-white/45 hover:text-white">
            <X size={14} />
          </button>
        )}
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<GuildRow>
        columns={[
          { key: 'guildNo', label: 'Nº', width: '90px', sortKey: 'guildNo',
            cell: (g) => <span className="text-xs font-mono text-white/55">{g.guildNo}</span> },
          { key: 'name', label: 'Nome', width: '2fr', sortKey: 'name',
            cell: (g) => <span className="text-sm font-bold text-white truncate">{g.name || '—'}</span> },
          { key: 'level', label: 'Nível', width: '90px', align: 'center', sortKey: 'level',
            cell: (g) => <span className="text-sm text-white/80">{g.level}</span> },
          { key: 'members', label: 'Membros', width: '110px', align: 'center', sortKey: 'members',
            cell: (g) => <span className="text-sm font-semibold text-white/80">{g.members}</span> },
        ]}
        rows={sorted}
        sort={sort}
        onSort={toggle}
        rowKey={(g) => g.guildNo}
        loading={loading}
        empty="Nenhuma guild encontrada"
        mobileTitle={(g) => g.name || `Guild ${g.guildNo}`}
        mobileSubtitle={(g) => `Nº ${g.guildNo} · Nv ${g.level} · ${g.members} membros`}
      />
    </div>
  );
}
